import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';

export interface FactoryClientData {
  id: string;
  industryId: string;
  industryName: string;
  name: string;
  mobile?: string;
  address?: string;
  dateOfRegistration?: Date | any;
  locationId?: string;
  securityCode?: string;
  requiredGuard?: number;
  contactName?: string;
  contactEmail?: string;
  contactNumber?: string;
  salary?: number;
  currentDeployment?: any;
  history?: any[];
}

export interface IndustryData {
  id: string;
  name: string;
}

export interface LocationData {
  id: string;
  name: string;
}

export const fetchIndustries = async (companyId?: string): Promise<IndustryData[]> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

 
  try {
    const industriesSnap = await getDocs(
      collection(db, 'Factory', uid, 'industries')
    );

    console.log('   Industries found:', industriesSnap.size);

    if (industriesSnap.size === 0) {
      console.warn('   ⚠️ No industries found!');
      console.warn('   Check if path exists: Factory/', uid, '/industries');
    }

    const industries = industriesSnap.docs.map((doc) => {
      const data = { id: doc.id, ...doc.data() };
      console.log('   📁 Industry:', doc.id, data);
      return data;
    }) as IndustryData[];

    return industries;
  } catch (error: any) {
    console.error('   ❌ Error fetching industries:', error);
    console.error('   Error code:', error.code);
    console.error('   Error message:', error.message);
    throw error;
  }
};

export const fetchLocations = async (): Promise<LocationData[]> => {
  const locationsSnap = await getDocs(collection(db, 'locations'));
  return locationsSnap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as LocationData[];
};

export const fetchFactoryClients = async (companyId?: string): Promise<FactoryClientData[]> => {
  // Use the specific company ID if provided, otherwise use current user UID
  // Expected path: Factory//industries/{industryId}/clients
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  
  try {
    const industries = await fetchIndustries(uid);
  
    if (industries.length === 0) {
      return [];
    }

    const allClients: FactoryClientData[] = [];

    for (const ind of industries) {
      const clientsPath = `Factory/${uid}/industries/${ind.id}/clients`;

      try {
        const clientsSnap = await getDocs(
          collection(db, 'Factory', uid, 'industries', ind.id, 'clients')
        );


        if (clientsSnap.size === 0) {
        }

        clientsSnap.forEach((doc) => {
          const clientData = {
            id: doc.id,
            industryId: ind.id,
            industryName: ind.name,
            ...doc.data(),
            contactName: doc.data().contactName || '',
            contactEmail: doc.data().contactEmail || '',
            contactNumber: doc.data().contactNumber || '',
          };
          console.log('   ✅ Client found:', doc.id, clientData);
          allClients.push(clientData as FactoryClientData);
        });
      } catch (error: any) {
        console.error(`   ❌ Error fetching clients from ${clientsPath}:`, error);
        console.error('   Error code:', error.code);
        console.error('   Error message:', error.message);
      }
    }

    console.log('📊 ===== TOTAL CLIENTS FETCHED:', allClients.length, '=====');
    if (allClients.length === 0) {
      console.warn('⚠️ No clients found. Check the console logs above for details.');
    }
    return allClients;
  } catch (error: any) {
    console.error('❌ Error in fetchFactoryClients:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
};

export const updateFactoryClient = async (
  clientId: string,
  industryId: string,
  data: Partial<FactoryClientData>,
  companyId?: string
): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  const clientRef = doc(
    db,
    'Factory',
    uid,
    'industries',
    industryId,
    'clients',
    clientId
  );

  const updateData: any = {
    name: data.name,
    mobile: data.mobile || null,
    address: data.address || null,
    industryName: data.industryName,
    locationId: data.locationId || null,
    requiredGuard: data.requiredGuard || 3,
    contactName: data.contactName || null,
    contactEmail: data.contactEmail || null,
    contactNumber: data.contactNumber || null,
    salary: data.salary || 0,
  };

  if (data.dateOfRegistration) {
    updateData.dateOfRegistration =
      data.dateOfRegistration instanceof Date
        ? data.dateOfRegistration.toISOString()
        : data.dateOfRegistration?.toDate
          ? data.dateOfRegistration.toDate().toISOString()
          : data.dateOfRegistration;
  } else {
    updateData.dateOfRegistration = null;
  }

  await updateDoc(clientRef, updateData);

  // NOTE: We intentionally do NOT update deployed guards' salaries here.
  // Guards should keep their original deployment salary for accurate historical calculations.
  // New deployments will pick up the new salary from the client document.
};

export const createFactoryClient = async (
  industryName: string,
  clientName: string,
  mobile: string,
  address: string,
  locationId: string,
  dateOfRegistration: Date | null,
  companyId: string,
  requiredGuard: number = 3,
  contactName: string = '',
  contactEmail: string = '',
  contactNumber: string = '',
  salary: number = 0
): Promise<void> => {
  if (!companyId) throw new Error('Company ID is required');
  if (!industryName.trim()) throw new Error('Industry name is required');
  if (!clientName.trim()) throw new Error('Client name is required');

  // Generate industry ID from name (slugify)
  const industryId = industryName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  // Create or update industry document
  const industryDocRef = doc(db, 'Factory', companyId, 'industries', industryId);
  await setDoc(industryDocRef, {
    name: industryName.trim(),
    createdAt: new Date()
  }, { merge: true });

  // Generate security code
  const firstThree = clientName.trim().substring(0, 3).toUpperCase();
  const randomSix = Math.floor(100000 + Math.random() * 900000);
  const securityCode = `${firstThree}-${randomSix}`;

  // Create client document
  const clientRef = doc(collection(industryDocRef, 'clients'));
  await setDoc(clientRef, {
    name: clientName.trim(),
    industryName: industryName.trim(),
    mobile: mobile.trim() || null,
    address: address.trim() || null,
    locationId: locationId || null,
    dateOfRegistration: dateOfRegistration || null,
    requiredGuard: requiredGuard || 3,
    contactName: contactName.trim() || null,
    contactEmail: contactEmail.trim() || null,
    contactNumber: contactNumber.trim() || null,
    salary: salary || 0,
    securityCode,
    type: 'Factory',
    companyId,
    createdAt: new Date(),
  });

  // Save security code reference
  const securityCodeRef = doc(collection(db, 'securityCodes'));
  await setDoc(securityCodeRef, {
    companyId,
    clientType: 'Factory',
    clientId: clientRef.id,
    securityCode,
  });
};

export const formatDate = (dateObj: any): string => {
  if (!dateObj) return 'N/A';
  if (typeof dateObj.toDate === 'function') {
    return dateObj.toDate().toLocaleDateString();
  }
  if (typeof dateObj === 'string') {
    const date = new Date(dateObj);
    return isNaN(date.getTime()) ? 'Invalid' : date.toLocaleDateString();
  }
  if (dateObj instanceof Date) {
    return dateObj.toLocaleDateString();
  }
  return 'N/A';
};

